import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Users, Search, X, Phone, Calendar, School,
  ShoppingBag, Building2, CreditCard, Tag, ChevronLeft,
  ChevronRight, ExternalLink, Plus, AlertCircle, CheckCircle2
} from 'lucide-react';
import api from '../../api/client';

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const B2B_COLOR   = { text: '#1D4ED8', bg: '#EEF2FF', border: '#BFDBFE' };
const B2C_COLOR   = { text: '#C84B0F', bg: '#FEF3EE', border: '#FDCFB5' };
const BOTH_COLOR  = { text: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' };
const LEGACY_COLOR = { text: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB' };

const CODE_TYPE_META = {
  b2b:    { label: 'B2B – School',  ...B2B_COLOR },
  b2c:    { label: 'B2C – Direct',  ...B2C_COLOR },
  legacy: { label: 'Legacy',        ...LEGACY_COLOR },
};

const UserTypeBadge = ({ isB2b, isB2c }) => {
  if (isB2b && isB2c) return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: BOTH_COLOR.bg, color: BOTH_COLOR.text, border: `1px solid ${BOTH_COLOR.border}` }}>B2B + B2C</span>
  );
  if (isB2b) return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: B2B_COLOR.bg, color: B2B_COLOR.text, border: `1px solid ${B2B_COLOR.border}` }}>B2B</span>
  );
  if (isB2c) return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, background: B2C_COLOR.bg, color: B2C_COLOR.text, border: `1px solid ${B2C_COLOR.border}` }}>B2C</span>
  );
  return null;
};

// ── Assign Code sub-component ─────────────────────────────────────────────
const AssignCodeForm = ({ userId, onAssigned }) => {
  const [code, setCode]         = useState('');
  const [saving, setSaving]     = useState(false);
  const [result, setResult]     = useState(null); // { success, message, slot }
  const inputRef                = React.useRef(null);

  const handleAssign = async () => {
    if (!code.trim()) return;
    setSaving(true); setResult(null);
    try {
      const res = await api.post(`/end-users/${userId}/assign-code`, { code: code.trim() });
      setResult({ success: true, message: res.data.message, slot: res.data.slot });
      setCode('');
      onAssigned(); // refresh codes list
    } catch (e) {
      setResult({ success: false, message: e.response?.data?.message || 'Failed to assign code' });
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '12px 24px', borderTop: '1px solid #F3EDE8', background: '#FFF8F4' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
        Assign Code to this user
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          ref={inputRef}
          value={code}
          onChange={e => { setCode(e.target.value.toUpperCase()); setResult(null); }}
          onKeyDown={e => e.key === 'Enter' && handleAssign()}
          placeholder="Enter code e.g. V2ISUFXA"
          style={{ flex: 1, padding: '8px 12px', border: '1.5px solid #F0DCC8', borderRadius: 8, fontSize: 13, fontFamily: 'monospace', fontWeight: 600, color: '#C84B0F', background: '#fff', outline: 'none', textTransform: 'uppercase', letterSpacing: 1 }}
        />
        <button
          onClick={handleAssign}
          disabled={saving || !code.trim()}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: saving || !code.trim() ? '#F0DCC8' : '#C84B0F', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: saving || !code.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', transition: 'background 0.15s' }}>
          {saving
            ? <><div style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /> Assigning…</>
            : <><Plus size={14} /> Assign Code</>}
        </button>
      </div>
      {result && (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
          color: result.success ? '#15803D' : '#B91C1C' }}>
          {result.success
            ? <CheckCircle2 size={14} color="#15803D" />
            : <AlertCircle size={14} color="#B91C1C" />}
          {result.message}
          {result.success && result.slot && (
            <span style={{ marginLeft: 4, fontSize: 11, padding: '1px 7px', borderRadius: 20,
              background: result.slot === 'login1' ? '#FEF3EE' : '#EEF2FF',
              color: result.slot === 'login1' ? '#C84B0F' : '#1D4ED8',
              border: `1px solid ${result.slot === 'login1' ? '#FDCFB5' : '#BFDBFE'}` }}>
              → {result.slot === 'login1' ? 'Login 1' : 'Login 2'}
            </span>
          )}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>
        If Login 1 is already occupied, code will be assigned to Login 2 automatically.
      </div>
    </div>
  );
};

// ── detail modal ────────────────────────────────────────────────────────────
const DetailModal = ({ userId, onClose }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('codes'); // 'codes' | 'payments'

  const loadDetail = useCallback(() => {
    setLoading(true);
    api.get(`/end-users/${userId}/detail`)
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const u = data?.user;
  const codes    = data?.codes    || [];
  const payments = data?.payments || [];

  const b2bCodes    = codes.filter(c => c.code_type === 'b2b');
  const b2cCodes    = codes.filter(c => c.code_type === 'b2c');
  const legacyCodes = codes.filter(c => c.code_type === 'legacy');

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.18)' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #F3EDE8' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              {loading || !u ? (
                <div style={{ width: 140, height: 20, background: '#F3EDE8', borderRadius: 6 }} />
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#FEF3EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, color: '#C84B0F', flexShrink: 0 }}>
                      {u.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: '#1C0A00' }}>{u.name}</div>
                      <div style={{ fontSize: 12, color: '#9CA3AF', display: 'flex', gap: 12, marginTop: 1 }}>
                        <span>{u.country_code}{u.mobile}</span>
                        {u.email && <span>{u.email}</span>}
                        <span>Joined {fmt(u.registered_at)}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <button onClick={onClose} style={{ background: '#FEF3EE', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#C84B0F', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <X size={16} />
            </button>
          </div>

          {/* Summary chips */}
          {!loading && data && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              {[
                { icon: Tag,        label: `${codes.length} codes redeemed`,     color: '#C84B0F', bg: '#FEF3EE' },
                { icon: Building2,  label: `${b2bCodes.length} B2B codes`,       color: '#1D4ED8', bg: '#EEF2FF' },
                { icon: ShoppingBag,label: `${b2cCodes.length} B2C codes`,       color: '#C84B0F', bg: '#FEF3EE' },
                { icon: CreditCard, label: `${payments.length} payments`,        color: '#15803D', bg: '#F0FDF4' },
              ].map(m => (
                <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 5, background: m.bg, border: `1px solid ${m.color}22`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: m.color }}>
                  <m.icon size={13} /> {m.label}
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0 }}>
            {[
              { key: 'codes',    label: `Codes (${codes.length})` },
              { key: 'payments', label: `Payments (${payments.length})` },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', borderBottom: `2px solid ${tab === t.key ? '#C84B0F' : 'transparent'}`, color: tab === t.key ? '#C84B0F' : '#9CA3AF', cursor: 'pointer', transition: 'all 0.15s' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, gap: 10, color: '#9CA3AF', fontSize: 13 }}>
              <div style={{ width: 18, height: 18, border: '2px solid #F0DCC8', borderTopColor: '#C84B0F', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Loading…
            </div>
          ) : tab === 'codes' ? (
            <div style={{ padding: '16px 24px' }}>
              {codes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 13 }}>No codes redeemed</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#FFF8F4' }}>
                      {['Code', 'Type', 'School / Batch', 'Your login', 'Redeemed on'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {codes.map((c, i) => {
                      const meta = CODE_TYPE_META[c.code_type] || CODE_TYPE_META.legacy;
                      const loginDate = c.user_login_slot === 'login1' ? c.stdOne_updated_at
                                      : c.user_login_slot === 'login2' ? c.stdTwo_updated_at
                                      : c.stdOne_updated_at || c.stdTwo_updated_at;
                      return (
                        <tr key={c.code} style={{ borderTop: '1px solid #FDF0E8', background: i % 2 === 0 ? '#fff' : '#FFFCFA' }}>
                          <td style={{ padding: '10px 12px' }}>
                            <code style={{ background: '#FEF3EE', color: '#C84B0F', padding: '2px 7px', borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{c.code}</code>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: meta.bg, color: meta.text, border: `1px solid ${meta.border}`, whiteSpace: 'nowrap' }}>
                              {meta.label}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <div style={{ fontSize: 13, color: '#1C0A00', fontWeight: 500 }}>{c.school_name || '—'}</div>
                            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{c.batch_name || ''}</div>
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                              background: c.user_login_slot === 'both' ? '#F0FDF4' : '#FFF8F4',
                              color: c.user_login_slot === 'both' ? '#15803D' : '#C84B0F' }}>
                              {c.user_login_slot === 'both' ? 'Both logins' : c.user_login_slot === 'login1' ? 'Login 1' : 'Login 2'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                            {loginDate ? fmt(loginDate) : 'Pre-migration'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <div style={{ padding: '16px 24px' }}>
              {payments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 13 }}>No successful payments</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#FFF8F4' }}>
                      {['Transaction ID', 'Batch', 'Amount', 'Date'].map(h => (
                        <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((p, i) => (
                      <tr key={p.id} style={{ borderTop: '1px solid #FDF0E8', background: i % 2 === 0 ? '#fff' : '#FFFCFA' }}>
                        <td style={{ padding: '10px 12px' }}>
                          <code style={{ background: '#F0FDF4', color: '#15803D', padding: '2px 7px', borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{p.transaction_id}</code>
                        </td>
                        <td style={{ padding: '10px 12px', color: '#1C0A00' }}>{p.batch_name || '—'}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 700, color: '#15803D' }}>₹{p.cost}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{fmtTime(p.payment_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

        {/* Assign code form — always visible */}
        <AssignCodeForm userId={userId} onAssigned={loadDetail} />

        <div style={{ padding: '10px 24px', borderTop: '1px solid #F3EDE8', fontSize: 12, color: '#9CA3AF', display: 'flex', justifyContent: 'space-between' }}>
          <span>User ID: {userId}</span>
          <span>{tab === 'codes' ? `${codes.length} codes` : `${payments.length} payments`}</span>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── main page ──────────────────────────────────────────────────────────────
export default function EndUsersPage() {
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [search, setSearch]         = useState('');
  const [userType, setUserType]     = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [detailId, setDetailId]     = useState(null);
  const searchTimer                 = useRef(null);

  const load = useCallback(async (page = 1, q = search, type = userType) => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ page, limit: 10, search: q, userType: type });
      const res = await api.get(`/end-users?${params}`);
      setUsers(res.data.data || []);
      setPagination(res.data.pagination || { page: 1, pages: 1, total: 0, limit: 10 });
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load users');
    } finally { setLoading(false); }
  }, [search, userType]);

  useEffect(() => { load(1); }, []);

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, val, userType), 400);
  };

  const handleType = (val) => {
    setUserType(val);
    load(1, search, val);
  };

  const handlePage = (p) => load(p, search, userType);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Users size={22} color="#C84B0F" />
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1C0A00', margin: 0 }}>End Users</h1>
          </div>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
            Registered customers — {pagination.total.toLocaleString('en-IN')} total
          </p>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {[
            { label: 'B2B – School order', ...B2B_COLOR },
            { label: 'B2C – Direct sale',  ...B2C_COLOR },
            { label: 'B2B + B2C',          ...BOTH_COLOR },
          ].map(m => (
            <span key={m.label} style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: m.bg, color: m.text, border: `1px solid ${m.border}` }}>
              {m.label}
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 14, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'center', gap: 8, background: '#FFF8F4', border: '1px solid #F0DCC8', borderRadius: 9, padding: '8px 12px' }}>
          <Search size={14} color="#C84B0F" />
          <input
            placeholder="Search by name or mobile…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#1C0A00', flex: 1 }} />
          {search && <button onClick={() => handleSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 }}><X size={13} /></button>}
        </div>

        {/* User type filter */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { val: '',    label: 'All users' },
            { val: 'b2b', label: 'B2B only'  },
            { val: 'b2c', label: 'B2C only'  },
          ].map(opt => (
            <button key={opt.val} onClick={() => handleType(opt.val)}
              style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', border: `1px solid ${userType === opt.val ? '#C84B0F' : '#F0DCC8'}`, background: userType === opt.val ? '#C84B0F' : '#fff', color: userType === opt.val ? '#fff' : '#6B4422', transition: 'all 0.15s' }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {error ? (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '16px 20px', color: '#B91C1C', fontSize: 13 }}>{error}</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#FFF8F4' }}>
                {['User', 'Mobile', 'Introduced by school', 'Type', 'Codes', 'Payments', 'Joined', ''].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #F0DCC8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: '48px 0', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#9CA3AF', fontSize: 13 }}>
                    <div style={{ width: 18, height: 18, border: '2px solid #F0DCC8', borderTopColor: '#C84B0F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Loading users…
                  </div>
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '48px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>No users found</td></tr>
              ) : users.map((u, i) => {
                const isB2b = Number(u.is_b2b) > 0;
                const isB2c = Number(u.is_b2c) > 0;
                const rowBg = isB2c && isB2b ? '#FDFAFF'
                            : isB2c          ? '#FFFCFA'
                            : '#fff';
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid #FDF0E8', background: i % 2 === 0 ? rowBg : '#FFFCFA', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#FFF3EC'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? rowBg : '#FFFCFA'}>

                    {/* User */}
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#FEF3EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#C84B0F', flexShrink: 0 }}>
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1C0A00', fontSize: 13 }}>{u.name}</div>
                          {u.email && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{u.email}</div>}
                        </div>
                      </div>
                    </td>

                    {/* Mobile */}
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#1C0A00', fontWeight: 500 }}>
                        <Phone size={12} color="#C84B0F" />
                        {u.country_code}{u.mobile}
                      </div>
                    </td>

                    {/* School */}
                    <td style={{ padding: '11px 16px' }}>
                      {u.school_names ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <School size={12} color="#1D4ED8" />
                          <span style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 500 }}>
                            {u.school_names.split(',').slice(0, 2).join(', ')}
                            {u.school_names.split(',').length > 2 && ` +${u.school_names.split(',').length - 2}`}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: '#D1D5DB', fontSize: 12 }}>—</span>
                      )}
                    </td>

                    {/* Type badge */}
                    <td style={{ padding: '11px 16px' }}>
                      <UserTypeBadge isB2b={isB2b} isB2c={isB2c} />
                    </td>

                    {/* Codes */}
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      {Number(u.total_codes) > 0
                        ? <span style={{ fontSize: 13, fontWeight: 700, color: '#C84B0F' }}>{u.total_codes}</span>
                        : <span style={{ color: '#D1D5DB' }}>0</span>}
                    </td>

                    {/* Payments */}
                    <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                      {Number(u.payment_count) > 0 ? (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#15803D' }}>{u.payment_count}</div>
                          <div style={{ fontSize: 11, color: '#9CA3AF' }}>₹{Number(u.total_paid).toLocaleString('en-IN')}</div>
                        </div>
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </td>

                    {/* Joined */}
                    <td style={{ padding: '11px 16px', fontSize: 12, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Calendar size={11} />
                        {fmt(u.registered_at)}
                      </div>
                    </td>

                    {/* Action */}
                    <td style={{ padding: '11px 16px' }}>
                      <button
                        onClick={() => setDetailId(u.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: '1px solid #F0DCC8', borderRadius: 8, background: '#FFF8F4', color: '#C84B0F', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                        <ExternalLink size={12} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #F0DCC8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: '#9CA3AF' }}>
                Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString('en-IN')} users
              </span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => handlePage(pagination.page - 1)} disabled={pagination.page <= 1}
                  style={{ padding: '6px 10px', border: '1px solid #F0DCC8', borderRadius: 8, background: '#fff', color: pagination.page <= 1 ? '#D1D5DB' : '#C84B0F', cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const p = Math.max(1, pagination.page - 2) + i;
                  if (p > pagination.pages) return null;
                  return (
                    <button key={p} onClick={() => handlePage(p)}
                      style={{ padding: '6px 11px', border: `1px solid ${p === pagination.page ? '#C84B0F' : '#F0DCC8'}`, borderRadius: 8, background: p === pagination.page ? '#C84B0F' : '#fff', color: p === pagination.page ? '#fff' : '#6B4422', fontWeight: p === pagination.page ? 700 : 400, cursor: 'pointer', fontSize: 13 }}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => handlePage(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
                  style={{ padding: '6px 10px', border: '1px solid #F0DCC8', borderRadius: 8, background: '#fff', color: pagination.page >= pagination.pages ? '#D1D5DB' : '#C84B0F', cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail modal */}
      {detailId && <DetailModal userId={detailId} onClose={() => setDetailId(null)} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}